import { OfficeMember } from '@/components/OfficeMember';
import { useAdminInvoiceReportPayeeContacts } from '@/context/services/admin_invoice_reports/AdminInvoiceReportPayeeContacts.service';
import {
  readViewerErrorMessages,
  usePutAdminInvoiceReportViewersService,
  type OfficeMember as OfficeMemberViewer,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportViewers.service';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonV2,
  Form,
  IconAdd,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { createAlreadySharedOptionDecorator } from './decorateAlreadySharedOption';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type FormValues = {
  office_member_ids: string[];
};

type AddViewerFormProps = {
  reportId: string;
  viewers: OfficeMemberViewer[];
  maxViewersCount?: number | null;
};

export const AddViewerForm: FC<AddViewerFormProps> = memo(
  ({ reportId, viewers, maxViewersCount }) => {
    const { t } = useTranslation();
    const notify = useToastAPI();
    const container = useGlobalContainerRef();
    const { mutateAsync, isPending } =
      usePutAdminInvoiceReportViewersService(reportId);

    const methods = useForm<FormValues>({
      defaultValues: { office_member_ids: [] },
    });
    const { control, reset, setError, setValue, getValues } = methods;

    const registeredIds = useMemo(
      () =>
        new Set(
          viewers.map((viewer) => viewer.id).filter((id): id is string => !!id)
        ),
      [viewers]
    );

    /**
     * Disable and re-render already-shared members in the picker with an
     * "already shared" tag, instead of a normal selectable checkbox row.
     */
    const decorateAlreadySharedOption = useMemo(
      () =>
        createAlreadySharedOptionDecorator(
          registeredIds,
          t('invoice_report_share.already_shared_tag')
        ),
      [registeredIds, t]
    );

    const payeeContacts = useAdminInvoiceReportPayeeContacts(reportId);
    const addablePayeeContacts = useMemo(
      () => payeeContacts.filter((contact) => !registeredIds.has(contact.id)),
      [payeeContacts, registeredIds]
    );

    /**
     * Rendered as a pinned, empty-options group above the office member
     * list (see `OfficeMember`'s `actionButton`), so clicking it just stages
     * the payee contacts into the form value directly, without going
     * through the select's own onChange/option-selection flow at all.
     */
    const onAddPayeeContacts = useCallback(() => {
      const current = getValues('office_member_ids');
      const additions = addablePayeeContacts.map((contact) => contact.id);
      setValue(
        'office_member_ids',
        Array.from(new Set([...current, ...additions])),
        { shouldValidate: true }
      );
    }, [addablePayeeContacts, getValues, setValue]);

    const actionButton = useMemo(
      () =>
        addablePayeeContacts.length > 0 ? (
          <div className={cx(styles.payeeContactAction)}>
            <ButtonV2
              size='xs'
              isSecondary
              leftIcon={<IconAdd />}
              buttonClassName={cx(styles.payeeContactActionButton)}
              onClick={onAddPayeeContacts}
            >
              {t('invoice_report_share.payee_contact_office_members')}
            </ButtonV2>
          </div>
        ) : undefined,
      [addablePayeeContacts, onAddPayeeContacts, t]
    );

    const onSubmit = useCallback(
      async ({ office_member_ids }: FormValues) => {
        const targets = office_member_ids.filter(
          (id) => !registeredIds.has(id)
        );
        try {
          await mutateAsync({ office_member_ids: targets });
          reset({ office_member_ids: [] });
          notify.success({
            title: t('invoice_report_share.added'),
            duration: 3,
          });
        } catch (error) {
          const messages = readViewerErrorMessages(error);
          const title = Array.isArray(messages)
            ? messages.join(',')
            : messages || t('system_error');
          setError('office_member_ids', { message: title });
          notify.error({ title, duration: 3 });
        }
      },
      [setError, mutateAsync, notify, registeredIds, reset, t]
    );

    /**
     * `required` doesn't work here: the value is a `string[]`, so an empty
     * `[]` is still truthy and passes react-hook-form's validation even when
     * nothing is selected. `OfficeMember` merges this `rules.validate` with
     * its own max-count check.
     */
    const rules = useMemo(
      () => ({
        validate: {
          notEmpty: (value: string | string[]) =>
            (Array.isArray(value) ? value.length > 0 : !!value) ||
            t('invoice_report_share.office_member_required'),
          notAllRegistered: (value: string | string[]) => {
            const ids = Array.isArray(value) ? value : [value];
            return (
              ids.some((id) => !registeredIds.has(id)) ||
              t('invoice_report_share.already_viewer')
            );
          },
        },
      }),
      [registeredIds, t]
    );

    const selectProps = useMemo(
      () => ({
        getPopupContainer: container,
        placeholder: t('invoice_report_share.office_member_placeholder'),
      }),
      [container, t]
    );

    return (
      <Form<FormValues> formMethod={methods} onSubmit={onSubmit}>
        <div className={cx(styles.container)}>
          <div className={cx(styles.row)}>
            <div className={cx(styles.select)}>
              <OfficeMember<FormValues>
                control={control}
                name='office_member_ids'
                inputId='admin-invoice-report-share-office-members'
                rules={rules}
                validateMaxCount={maxViewersCount ?? undefined}
                selectProps={selectProps}
                actionButton={actionButton}
                decorateOption={decorateAlreadySharedOption}
              />
            </div>
            <ButtonV2
              type='submit'
              size='sm'
              isDisabled={isPending}
              testId='admin-invoice-report-share-add-button'
            >
              {t('invoice_report_share.add')}
            </ButtonV2>
          </div>
        </div>
      </Form>
    );
  }
);
AddViewerForm.displayName = 'AddViewerForm';
